import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { CheckCircle, Package, ArrowRight, Loader2, XCircle } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { API } from '@/config';

export default function OrderSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [order, setOrder] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const { fetchCart } = useCart();

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      try {
        const statusResponse = await axios.get(`${API}/checkout/status/${sessionId}`);
        
        if (statusResponse.data.payment_status === 'paid') {
          setStatus('success');
          // Fetch order details
          try {
            const orderResponse = await axios.get(`${API}/orders/session/${sessionId}`);
            setOrder(orderResponse.data);
          } catch (orderError) {
            console.error('Error fetching order:', orderError);
          }
          // Refresh cart (should be empty now)
          fetchCart();
        } else if (statusResponse.data.status === 'expired') {
          setStatus('expired');
        } else if (attempts < 5) {
          // Poll again after 2 seconds
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('pending');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (attempts < 5) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    checkPaymentStatus();
  }, [sessionId, attempts, fetchCart]);

  if (status === 'loading') {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center px-4">
        <Loader2 className="h-12 w-12 animate-spin text-pockett-purple mb-4" />
        <p className="text-muted-foreground">Confirming your payment...</p>
      </div>
    );
  }

  if (status === 'error' || status === 'expired') {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center px-4 text-center">
        <XCircle className="h-20 w-20 text-destructive mb-6" />
        <h1 className="font-heading font-bold text-2xl text-pockett-gray mb-2">
          {status === 'expired' ? 'Payment Session Expired' : 'Something went wrong'}
        </h1>
        <p className="text-muted-foreground mb-8 max-w-md">
          {status === 'expired' 
            ? 'Your payment session has expired. Please try again.'
            : 'We couldn\'t confirm your payment. Please contact us if you were charged.'}
        </p>
        <Button 
          asChild
          className="bg-pockett-purple hover:bg-pockett-purple/90 rounded-full px-8"
          data-testid="return-to-cart-btn"
        >
          <Link to="/cart">Return to Cart</Link>
        </Button>
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center px-4 text-center">
        <Package className="h-20 w-20 text-pockett-purple mb-6" />
        <h1 className="font-heading font-bold text-2xl text-pockett-gray mb-2">
          Payment Processing
        </h1>
        <p className="text-muted-foreground mb-8 max-w-md">
          Your payment is still being processed. You'll receive an email confirmation once it's complete.
        </p>
        <Button 
          asChild
          className="bg-pockett-purple hover:bg-pockett-purple/90 rounded-full px-8"
          data-testid="continue-shopping-btn"
        >
          <Link to="/shop">Continue Shopping</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 md:py-20 px-4 md:px-8">
      <div className="max-w-2xl mx-auto">
        {/* Success Message */}
        <div className="text-center mb-12 animate-fade-in">
          <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-12 w-12 text-green-600" />
          </div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-pockett-gray mb-4">
            Thank you for your order!
          </h1>
          <p className="text-muted-foreground text-lg">
            Your payment was successful. We'll start preparing your handmade treasures right away!
          </p>
        </div>

        {/* Order Details */}
        {order && (
          <div className="bg-white rounded-2xl border border-border/50 p-6 md:p-8 mb-8 animate-fade-in animate-delay-100">
            <h2 className="font-heading font-semibold text-xl text-pockett-gray mb-6">
              Order Details
            </h2>
            
            <div className="space-y-4 mb-6">
              {order.items.map((item, index) => (
                <div 
                  key={`${item.product_id}-${index}`}
                  className="flex gap-4"
                  data-testid={`order-item-${item.product_id}`}
                >
                  <img 
                    src={item.image_url} 
                    alt={item.product_name}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-pockett-gray">{item.product_name}</p>
                    {item.variant_name && (
                      <p className="text-sm text-muted-foreground">{item.variant_name}</p>
                    )}
                    <p className="text-sm text-muted-foreground">Qty: {item.quantity}</p>
                  </div>
                  <p className="font-medium text-pockett-gray">
                    ${(item.price * item.quantity).toFixed(2)}
                  </p>
                </div>
              ))}
            </div>

            <div className="border-t border-border pt-4">
              <div className="flex justify-between">
                <span className="font-semibold text-pockett-gray">Total Paid</span>
                <span className="font-heading font-bold text-xl text-pockett-purple">
                  ${order.total.toFixed(2)} AUD
                </span>
              </div>
            </div>
          </div>
        )}

        {/* What's Next */}
        <div className="bg-gradient-to-br from-pockett-purple/5 via-pockett-pink/5 to-pockett-blue/5 rounded-2xl p-6 md:p-8 mb-8 animate-fade-in animate-delay-200">
          <h2 className="font-heading font-semibold text-xl text-pockett-gray mb-4">
            What happens next?
          </h2>
          <ul className="space-y-3 text-muted-foreground">
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-pockett-purple text-white flex items-center justify-center flex-shrink-0 text-sm font-bold">1</span>
              <span>I'll lovingly handcraft your items</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-pockett-pink text-white flex items-center justify-center flex-shrink-0 text-sm font-bold">2</span>
              <span>Your order will be carefully packaged</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-pockett-blue text-white flex items-center justify-center flex-shrink-0 text-sm font-bold">3</span>
              <span>You'll receive shipping updates via email</span>
            </li>
          </ul>
        </div>

        {/* CTA */}
        <div className="text-center animate-fade-in animate-delay-300">
          <Button 
            asChild
            className="bg-pockett-purple hover:bg-pockett-purple/90 rounded-full px-8 h-12 font-semibold"
            data-testid="continue-shopping-btn"
          >
            <Link to="/shop">
              Continue Shopping
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
